import React, { useMemo, useState } from "react";
import { Twitter, Linkedin, Link2, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../common/Primitives";

/**
 * DigestShareCard — one-tap share of a founder's weekly digest snapshot
 * to Twitter / LinkedIn with a copy-link fallback. Reads the same payload
 * that /startup/investor-digest-preview returns.
 *
 * The share URL is the public marketing origin (window.location.origin) with
 * a UTM ref so click-throughs are attributable to the founder. Nothing about
 * the private profile is exposed — only the traction stats they'd already brag
 * about (trust score, watchers, sector, ask).
 */
const fmtAsk = (n) => {
  if (!n) return "";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
};

const composeMessage = (d) => {
  const name = d.startup_name || "Our startup";
  const pieces = [`${name} · this week on VenturaZ 🚀`];
  const stats = [];
  if (typeof d.trust_score === "number") {
    const deltaStr =
      d.trust_delta_7d > 0
        ? ` (▲${d.trust_delta_7d} this week)`
        : d.trust_delta_7d < 0
          ? ` (▼${Math.abs(d.trust_delta_7d)})`
          : "";
    stats.push(`✓ Trust Score ${d.trust_score}/100${deltaStr}`);
  }
  if (typeof d.watchers === "number" && d.watchers > 0) {
    stats.push(`✓ ${d.watchers} investor${d.watchers === 1 ? "" : "s"} watching`);
  }
  const ask = fmtAsk(d.funding_ask);
  if (ask) stats.push(`✓ Raising ${ask}${d.business_stage ? ` · ${d.business_stage}` : ""}`);
  if ((d.verified_pills || []).length) {
    stats.push(`✓ Verified: ${d.verified_pills.slice(0, 4).join(" · ")}`);
  }
  if (stats.length) pieces.push(stats.join("\n"));
  pieces.push("Fundraise with clarity → ");
  return pieces.join("\n\n");
};

export default function DigestShareCard({ data }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "https://venturaz.com";
    return `${window.location.origin}?ref=founder_digest`;
  }, []);
  const text = useMemo(() => (data ? composeMessage(data) : ""), [data]);
  const fullMessage = `${text}${shareUrl}`;

  if (!data) return null;

  const openTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=520");
  };
  const openLinkedIn = () => {
    // LinkedIn only takes a URL — it scrapes OG tags from the landing page
    // for the preview card. Copy the composed caption to the clipboard first
    // so the founder can paste it into LinkedIn's compose sheet.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(fullMessage).catch(() => {});
      toast.success("Caption copied — paste into LinkedIn.");
    }
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=520");
  };
  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(fullMessage);
      setCopied(true);
      toast.success("Digest caption + link copied.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — long-press to copy manually.");
    }
  };

  return (
    <Card testId="digest-share-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="overline flex items-center gap-1.5">
            <Share2 size={12} className="text-gold" /> Share this digest
          </div>
          <div className="font-heading text-ink mt-1">Turn your week&rsquo;s traction into a post</div>
          <div className="text-xs text-ink-soft mt-1 max-w-lg">
            One-tap share to X or LinkedIn. Only public traction stats go out —
            never your private profile or documents.
          </div>
        </div>
      </div>

      <pre
        className="mt-4 whitespace-pre-wrap break-words rounded-md border border-navy/10 bg-navy/[0.03] p-3 text-xs text-ink-soft font-sans max-h-40 overflow-y-auto"
        data-testid="digest-share-preview-text"
      >
        {fullMessage}
      </pre>

      <div className="mt-3 flex items-center flex-wrap gap-2">
        <button
          onClick={openTwitter}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-black text-white text-xs font-heading font-semibold hover:bg-gray-800 transition-colors"
          data-testid="digest-share-twitter"
          title="Share to X (Twitter)"
        >
          <Twitter size={13} /> Share on X
        </button>
        <button
          onClick={openLinkedIn}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0A66C2] text-white text-xs font-heading font-semibold hover:bg-[#084b91] transition-colors"
          data-testid="digest-share-linkedin"
          title="Share to LinkedIn"
        >
          <Linkedin size={13} /> Share on LinkedIn
        </button>
        <button
          onClick={copyBoth}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-xs font-heading font-semibold transition-colors ${
            copied
              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
              : "border-navy/20 text-ink hover:border-gold hover:text-[#8A6600]"
          }`}
          data-testid="digest-share-copy"
        >
          {copied ? <><Check size={13} /> Copied</> : <><Link2 size={13} /> Copy caption</>}
        </button>
      </div>
    </Card>
  );
}
